import React, {useContext, useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {IOrderModel} from '@src/@interfaces/models/Order/IOrderModel';
import {Props} from '@src/components/Form/FormField';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import app from '@src/helpers/app';
import RenderIf from '@src/components/RenderIf/RenderIf';
import ErrorRepresentation from '@src/components/ErrorRepresentation/ErrorRepresentation';
import Translate from '@src/components/Translate/Translate';
import LoadingIndicator from '@src/components/LoadingIndicator/LoadingIndicator';
import {IOrderTaskModel} from '@src/@interfaces/models/Order/IOrderTaskModel';
import {FormTabsContext} from '@src/contexts/FormTabsContext';
import TaskForm from './TaskForm';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';

export default function OrderTasksField<Model extends IOrderModel = IOrderModel>(props: Props<Model>) {
    const tabName = props.tabName;
    const tabsContext = useContext(FormTabsContext);
    const [designContext] = useContext(FormFieldDesignContext);
    const location = useLocation();
    const isActiveTab = !tabsContext || !tabName || tabsContext.isActiveTab(location, tabName);

    const queryClient = useQueryClient();
    const [newTasks, setNewTasks] = useState<IOrderTaskModel<any>[]>([]);
    const [queryKey, makeOnDelete, makeOnDeleteTask] = useMemo(() => {
        const queryKey = app('queryKeys').getCustomQueryOfExactModelQueryKey(
                'Orders', props.model?.id, 'tasks'
        );

        const onDelete = (index: number) => async () => {
            setNewTasks(list => {
                list.splice(index, 1);

                return list.slice();
            });

            return true;
        };

        const onDeleteTask = (taskId: number | string) => () => {
            return app('http').route('orders.tasks.destroy', {
                id: String(props.model?.id),
                taskId: String(taskId),
            }, {
                headers: {'content-type': 'application/json'},
                body: {'controller-name': 'Orders'}
            }).then(value => {
                queryClient.refetchQueries({queryKey, exact: true}).then();

                return value;
            });
        };

        return [queryKey, onDelete, onDeleteTask];
    }, [props.model?.id, queryClient]);

    useEffect(() => {
        queryClient.invalidateQueries({queryKey, exact: true}).then();
    }, [queryClient, queryKey]);

    const {isFetching, error, data} = useQuery<IOrderTaskModel<any>[], Error>(queryKey, () => {
        return app('http').route('orders.tasks.index', {
            id: String(props.model?.id)
        }, {
            headers: {'content-type': 'application/json'},
            body: {'controller-name': 'Orders'},
        });
    }, {
        useErrorBoundary: false,
        enabled: Boolean(props.model?.id) && isActiveTab,
        staleTime: 5 * 60 * 1000,
    });

    const orderModel = props.model;
    const tasksNumber = (data?.length ?? 0) + newTasks.length;

    useEffect(() => {
        if ((data != null || tasksNumber > 0) && tabName && tabsContext) {
            tabsContext.setBadge(tabName, {label: String(tasksNumber)});
        }
    }, [tabsContext, tabName, tasksNumber, data]);

    return <div>
        <div className="form-label d-flex align-items-center mb-3">
            <Translate message="OrdersShow.tabs.tasks" tag="label" className="mb-0 me-2"/>

            <button
                    type="button"
                    className="btn btn-outline-info btn-sm"
                    onClick={() => setNewTasks(list => [{type: null, payload: null}, ...list])}
                    disabled={designContext.fieldsAreDisabled || designContext.fieldsAreReadOnly || !orderModel}
            >
                <i className="fa fa-plus"/>
            </button>
        </div>

        <RenderIf expression={error}>
            <ErrorRepresentation error={error as Error}/>
        </RenderIf>

        <RenderIf expression={!tasksNumber}>
            <div className="d-flex py-5 px-2 justify-content-center align-items-center text-muted">
                <RenderIf expression={isFetching}>
                    <LoadingIndicator type="border" size="sm" className="me-2"/>
                    <Translate message="App.loading"/>
                </RenderIf>
                <RenderIf expression={!isFetching}>
                    <Translate message="TaskForm.messages.no-tasks" className="text-muted"/>
                </RenderIf>
            </div>
        </RenderIf>

        <RenderIf expression={Boolean(newTasks.length)}>
            {newTasks.map((task, index) => {
                return !orderModel ? null : <TaskForm
                        key={`${task.type}-${index}`}
                        task={task}
                        order={orderModel}
                        onDelete={makeOnDelete(index)}
                />;
            })}
        </RenderIf>

        <RenderIf expression={Boolean(data?.length)}>
            {data?.map((task) => {
                return !orderModel ? null : <TaskForm
                        key={`${task.type}-${task.id}`}
                        task={task}
                        order={orderModel}
                        onDelete={makeOnDeleteTask(task.id as number)}
                        deleteWithConfirm
                />;
            })}
        </RenderIf>
    </div>;
}
